import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

// ── Backend Types ────────────────────────────────────────────────────────────

export type BackendManagerItem = {
	rank: number;
	userId: string;
	fullName: string;
	salesCount: number;
	totalRevenue: number;
	totalCollected: number;
	collectionRate: number;
	avgSalePrice: number;
};

// ── Frontend Types ───────────────────────────────────────────────────────────

export type ManagerStat = {
	id: string;
	name: string;
	initials: string;
	colorVariant: "blue" | "green" | "sky" | "violet" | "amber";
	building: string;
	totalSales: number;
	totalAmount: number; // UZS
	collectedAmount: number; // UZS
	collectionRate: number; // %
	avgDealSize: number; // UZS
};

export type ManagersReportParams = {
	dateFrom?: string;
	dateTo?: string;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const COLOR_VARIANTS: Array<"blue" | "green" | "sky" | "violet" | "amber"> = [
	"blue",
	"violet",
	"green",
	"sky",
	"amber",
];

function getInitials(fullName: string): string {
	const parts = fullName.trim().split(/\s+/);
	if (parts.length >= 2) {
		return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
	}
	return (fullName[0] ?? "?").toUpperCase();
}

function getColorVariant(index: number): "blue" | "green" | "sky" | "violet" | "amber" {
	return COLOR_VARIANTS[index % COLOR_VARIANTS.length] ?? "blue";
}

// ── Mapper ───────────────────────────────────────────────────────────────────

function mapBackendManager(manager: BackendManagerItem, index: number): ManagerStat {
	return {
		id: manager.userId,
		name: manager.fullName,
		initials: getInitials(manager.fullName),
		colorVariant: getColorVariant(index),
		building: "", // Backend does not have building names in this report
		totalSales: manager.salesCount,
		totalAmount: manager.totalRevenue,
		collectedAmount: manager.totalCollected,
		collectionRate: manager.collectionRate,
		avgDealSize: manager.avgSalePrice,
	};
}

// ── API ──────────────────────────────────────────────────────────────────────

export async function getManagersReport(params?: ManagersReportParams): Promise<ManagerStat[]> {
	const response = await apiClient.get<{ success: boolean; data: BackendManagerItem[] }>(
		endpoints.reports.managers,
		{ params }
	);
	return (response.data.data || []).map(mapBackendManager);
}
